import { useMemo, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import TableUsuarios from '../Components/TableUsuarios';
import Api from '../Auth/Api';
import Select from 'react-select';
import { Controller, useForm } from "react-hook-form";
import { Badge, Modal, Toast, Offcanvas } from 'react-bootstrap';
import { decodeJWT } from "../Components/jwtUtils";
import DropdownMenu from '../Components/DropdownMenu';
import { customStyles } from '../utils/selectStyles';
import { DEFAULT_LISTA_TIPO, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../utils/paginationDefaults';
import { useAjustes } from '../Context/AjustesContext';
import { gestorPodeCadastrarEditar, gestorPodeResetarSenha, gestorMostrarCampoCursos, exibirCampoCursosNoFormulario, cursosObrigatoriosNoFormulario } from '../utils/gestorPermissoes';
import { exportarUsuariosExcel } from '../utils/exportUsuariosExcel';
import ModalImportarUsuarios from '../Components/Usuarios/ModalImportarUsuarios';

const formatCPF = (value) => {
    return value
        .replace(/\D/g, '')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

function ListaUsuarios() {

    const navigate = useNavigate();
    const token = JSON.parse(localStorage.getItem("user_token"));
    const nivel = decodeJWT(token).nivel;
    const { ajustes } = useAjustes();
    const gestorPermiteEditar = gestorPodeCadastrarEditar(ajustes);
    const gestorPermiteCursos = gestorMostrarCampoCursos(ajustes, nivel);
    const gestorPermiteResetarSenha = gestorPodeResetarSenha(ajustes);
    // Importação em massa: Admin sempre; Gestor/RH conforme permissão por empresa.
    const podeImportarUsuarios = nivel === 'Administrador'
        || (nivel === 'Gestor' && Number(ajustes?.gestor_pode_importar_usuario ?? 0) !== 0)
        || (nivel === 'RH' && Number(ajustes?.rh_pode_importar_usuario ?? 0) !== 0);
    // Baixar lista em Excel: Admin sempre; Gestor/RH conforme permissão por empresa.
    const podeExportarUsuarios = nivel === 'Administrador'
        || (nivel === 'Gestor' && Number(ajustes?.gestor_pode_exportar_usuario ?? 1) !== 0)
        || (nivel === 'RH' && Number(ajustes?.rh_pode_exportar_usuario ?? 1) !== 0);

    const [showModalImportar, setShowModalImportar] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    const [selectedTipo, setSelectedTipo] = useState(DEFAULT_LISTA_TIPO);
    const [globalFilter, setGlobalFilter] = useState('');
    const [showDrawerFiltros, setShowDrawerFiltros] = useState(false);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    
    /** Favoritos vs lista completa (não confundir com Tipo de perfil). */
    const filterLista = useMemo(
        () => [
            { value: "todos", label: "Todos" },
            { value: "favoritos", label: "Favoritos" },
        ],
        []
    );

    const LoadingScreen = () => {
        return (
            <div className="d-flex align-items-center justify-content-center p-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        )
    }

    const [selectedFilter, setSelectedFilter] = useState("Aluno");
    const [showModalEditarUsuario, setShowModalEditarUsuario] = useState(false);
    const [showModalResetSenha, setShowModalResetSenha] = useState({ show: false });
    const [toast, setToast] = useState({});
    const [showToast, setShowToast] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showModalPesquisa, setShowModalPesquisa] = useState({ show: false, data: null });
    const [showModalStatus, setShowModalStatus] = useState({ show: false, id: null, nome: '', status: false });
    const [showModalDeletarUsuario, setShowModalDeletarUsuario] = useState({ show: false, id: null, nome: '' });
    const [confirmDeleteText, setConfirmDeleteText] = useState('');
    const [cursosRecomendados, setCursosRecomendados] = useState([]);
    const [confirmandoSubstituirCursos, setConfirmandoSubstituirCursos] = useState(false);
    const [editarSomenteCursos, setEditarSomenteCursos] = useState(false);
    const [exportandoUsuarios, setExportandoUsuarios] = useState(false);

    const { register, handleSubmit, control, setValue, watch, formState: { errors } } = useForm();

    const fetchCursosRecomendados = useCallback(async (cargoId) => {
        if (!cargoId) {
            setCursosRecomendados([]);
            return [];
        }

        try {
            const response = await Api.get(`api/cargos/${cargoId}/cursos`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCursosRecomendados(response.data || []);
            return response.data || [];
        } catch (error) {
            console.error('Erro ao buscar cursos recomendados:', error);
            setCursosRecomendados([]);
            return [];
        }
    }, [token]);

    const mergeCursosComRecomendados = (cursosAtuais, recomendados) => {
        const map = new Map();
        (cursosAtuais || []).forEach((curso) => map.set(curso.value, curso));
        (recomendados || []).forEach((curso) => {
            map.set(curso.id, { value: curso.id, label: curso.nome });
        });
        return Array.from(map.values());
    };

    const handleConfirmarSubstituirCursos = () => {
        const cursosDoCargo = cursosRecomendados.map((curso) => ({
            value: curso.id,
            label: curso.nome
        }));
        setValue('cursos', cursosDoCargo);
        setConfirmandoSubstituirCursos(false);
    };

    const handleFecharModalEditarUsuario = () => {
        setConfirmandoSubstituirCursos(false);
        setEditarSomenteCursos(false);
        setShowModalEditarUsuario(false);
    };

    const syncUnidadePrincipal = useCallback((unidadesSelecionadas, principalAtual, isGestor) => {
        if (!isGestor) {
            return;
        }

        if (!unidadesSelecionadas?.length) {
            setValue('unidade_principal', null);
            return;
        }

        if (unidadesSelecionadas.length === 1) {
            setValue('unidade_principal', unidadesSelecionadas[0]);
            return;
        }

        const valores = unidadesSelecionadas.map((item) => item.value);
        if (principalAtual?.value && valores.includes(principalAtual.value)) {
            return;
        }

        setValue('unidade_principal', null);
    }, [setValue]);

    const watchedUnidadeEdit = watch('unidade');
    const watchedUnidadePrincipalEdit = watch('unidade_principal');
    // Níveis que podem ter múltiplas unidades (Gestor e RH).
    const ehMultiUnidade = (n) => n === 'Gestor' || n === 'RH';
    const nivelAtualForm = watch('nivel')?.value || watch('nivel')?.label;
    const isGestorEdit = ehMultiUnidade(nivelAtualForm);

    useEffect(() => {
        if (isGestorEdit) {
            syncUnidadePrincipal(watchedUnidadeEdit, watchedUnidadePrincipalEdit, true);
        }
    }, [watchedUnidadeEdit, watchedUnidadePrincipalEdit, isGestorEdit, syncUnidadePrincipal]);

    const [dataUsuarios, setDataUsuarios] = useState([]);
    const [selectedUnidade, setSelectedUnidade] = useState(null);

    useEffect(() => {
        setLoading(true);
        async function fetchData() {
            try {
                const response = await Api.get(`api/listar-usuarios/nivel/${selectedFilter}/${selectedTipo}`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    params: {
                        unidade: selectedUnidade ? selectedUnidade.value : undefined,
                    }
                });
                setDataUsuarios(response.data);
                setLoading(false);
            } catch (error) {
                console.error(error.response.data.message)
                setLoading(false);
                setDataUsuarios([]);
            }
        }
        fetchData();
    }, [token, selectedFilter, selectedTipo, selectedUnidade, refreshKey]);

    // Filtrar dados com base no globalFilter
    const filteredDataUsuarios = useMemo(() => {
        if (!globalFilter) return dataUsuarios;
        return dataUsuarios.filter(usuario =>
            usuario.nome_completo?.toLowerCase().includes(globalFilter.toLowerCase()) ||
            usuario.codigo?.toString().includes(globalFilter) ||
            usuario.email?.toLowerCase().includes(globalFilter.toLowerCase()) ||
            usuario.unidade?.toLowerCase().includes(globalFilter.toLowerCase())
        );
    }, [dataUsuarios, globalFilter]);

    const [dataCursos, setDataCursos] = useState([]);
    useEffect(() => {
        async function fetchData() {
            try {
                const response = await Api.get(`api/listar-cursos`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setDataCursos(response.data);
            } catch (error) {
                console.error(error);
            }
        }
        fetchData();
    }, [token, toast]);

    const [dataUnidades, setDataUnidades] = useState([]);
    useEffect(() => {
        async function fetchData() {
            try {
                const response = await Api.get(`api/listar-unidades`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setDataUnidades(response.data);
            } catch (error) {
                console.error(error);
            }
        }
        fetchData();
    }, [token]);

    const dataNiveis = useMemo(() => {
        const niveis = [
            { value: 'Aluno', label: 'Aluno' },
            { value: 'Gestor', label: 'Gestor' },
            { value: 'RH', label: 'RH' },
            { value: 'Administrador', label: 'Administrador' },
        ];
        if (nivel === 'Gestor') {
            return niveis.filter(nivel => nivel.value === 'Aluno');
        }
        return niveis;
    }, [nivel]);

    // Função para validar CPF
    const validarCPF = (cpf) => {
        cpf = cpf.replace(/\D/g, '');
        
        if (cpf.length !== 11) return false;
        
        // Verifica se todos os dígitos são iguais
        if (/^(\d)\1{10}$/.test(cpf)) return false;
        
        // Validação do primeiro dígito verificador
        let soma = 0;
        for (let i = 0; i < 9; i++) {
            soma += parseInt(cpf.charAt(i)) * (10 - i);
        }
        let resto = 11 - (soma % 11);
        let digitoVerificador1 = resto === 10 || resto === 11 ? 0 : resto;
        
        if (digitoVerificador1 !== parseInt(cpf.charAt(9))) return false;
        
        // Validação do segundo dígito verificador
        soma = 0;
        for (let i = 0; i < 10; i++) {
            soma += parseInt(cpf.charAt(i)) * (11 - i);
        }
        resto = 11 - (soma % 11);
        let digitoVerificador2 = resto === 10 || resto === 11 ? 0 : resto;
        
        if (digitoVerificador2 !== parseInt(cpf.charAt(10))) return false;
        
        return true;
    };

    const [loadingSave, setLoadingSave] = useState(false);

    const onSubmitEditarUsuario = async (data) => {
        setLoadingSave(true);
        data.cursos = (data.cursos || []).map((curso) => curso.value);

        if (editarSomenteCursos) {
            try {
                const response = await Api.post(`api/editar-usuario/${data.id}`, {
                    id: data.id,
                    cursos: data.cursos,
                    somente_cursos: true,
                }, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setToast({ header: 'Sucesso!', body: response.data.message, type: 'toast-success' })
                setShowToast(true);
                handleFecharModalEditarUsuario();

                const listResponse = await Api.get(`api/listar-usuarios/nivel/${selectedFilter}/${selectedTipo}`, {
                    headers: { Authorization: `Bearer ${token}` },
                    params: {
                        unidade: selectedUnidade ? selectedUnidade.value : undefined,
                    }
                });
                setDataUsuarios(listResponse.data);
            } catch (error) {
                const errorMessage = error.response?.data?.message || 'Erro ao atribuir cursos';
                setToast({ header: 'Ops!', body: errorMessage, type: 'toast-error' })
                setShowToast(true);
            }
            setLoadingSave(false);
            return;
        }

        // Limpar CPF (remover formatação) e permitir vazio para Administrador
        if (data.cpf) {
            data.cpf = data.cpf.replace(/\D/g, '');
        }
        if (nivel === 'Administrador' && !data.cpf) {
            delete data.cpf;
        }

        try {
            const response = await Api.post(`api/editar-usuario/${data.id}`, data, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            setToast({ header: 'Sucesso!', body: response.data.message, type: 'toast-success' })
            setShowToast(true);
            handleFecharModalEditarUsuario();
            
            // Atualizar a tabela apenas em caso de sucesso
            const fetchData = async () => {
                try {
                    const response = await Api.get(`api/listar-usuarios/nivel/${selectedFilter}/${selectedTipo}`, {
                        headers: {
                            Authorization: `Bearer ${token}`
                        },
                        params: {
                            unidade: selectedUnidade ? selectedUnidade.value : undefined,
                        }
                    });
                    setDataUsuarios(response.data);
        } catch (error) {
                    console.error("Erro ao buscar usuários:", error);
                    setDataUsuarios([]);
                }
            };
            fetchData();
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Erro ao editar usuário';
            setToast({ header: 'Ops!', body: errorMessage, type: 'toast-error' })
            setShowToast(true);
            console.error("Erro ao editar usuário:", error)
        }
        setLoadingSave(false);
    }

    const handleResetPassword = async (id) => {
        try {
            const response = await Api.post(`api/resetar-senha/${id}`, {}, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            setToast({ header: 'Sucesso!', body: response.data.message, type: 'toast-success' })
        } catch (error) {
            setToast({ header: 'Ops!', body: error.response.data.message, type: 'toast-error' })
            console.error("Erro ao resetar senha:", error)
        }
        setShowModalResetSenha({ show: false })
        setShowToast(true)
    };

    const handleFavoritar = useCallback(async (id) => {
        try {
            await Api.post(`api/favoritar/${id}`, {}, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            // Atualiza o estado do usuário na lista
            setDataUsuarios(prevUsers =>
                prevUsers.map(user =>
                    user.id === id ? { ...user, favorito: true } : user
                )
            );
        } catch (error) {
            setToast({ header: 'Ops!', body: "Aconteceu um erro :(", type: 'toast-error' });
            setShowToast(true);
            console.error("Erro ao favoritar usuário:", error);
        }
    }, [token]);

    const handleDesfavoritar = useCallback(async (id) => {
        try {
            await Api.post(`api/desfavoritar/${id}`, {}, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            // Atualiza o estado do usuário na lista
            setDataUsuarios(prevUsers =>
                prevUsers.map(user =>
                    user.id === id ? { ...user, favorito: false } : user
                )
            );
        } catch (error) {
            console.error("Erro ao desfavoritar usuário:", error);
        }
    }, [token]);

    const handleShowPesquisa = useCallback(async (id) => {
        try {
            const response = await Api.get(`api/pesquisa-satisfacao-usuario/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            
            if (response.data && response.data.pesquisa_satisfacao) {
            try {
                    const pesquisaObj = JSON.parse(response.data.pesquisa_satisfacao);

                // Transformar o objeto de perguntas em um array
                const pesquisaArray = Object.entries(pesquisaObj).map(([key, value]) => ({
                    pergunta: value.pergunta,
                    resposta: value.resposta,
                    data_resposta: new Date().toISOString()
                }));

                setShowModalPesquisa({ show: true, data: pesquisaArray });
            } catch (error) {
                console.error("Erro ao processar dados da pesquisa:", error);
                    setToast({ header: 'Ops!', body: "Erro ao processar dados da pesquisa", type: 'toast-error' });
                setShowToast(true);
                setShowModalPesquisa({ show: true, data: [] });
            }
        } else {
            setShowModalPesquisa({ show: true, data: [] });
        }
        } catch (error) {
            console.error("Erro ao buscar pesquisa do usuário:", error);
            setToast({ header: 'Ops!', body: "Erro ao buscar pesquisa do usuário", type: 'toast-error' });
            setShowToast(true);
            setShowModalPesquisa({ show: true, data: [] });
        }
    }, [token]);

    const [dataCargos, setDataCargos] = useState([]);
    useEffect(() => {
        async function fetchData() {
            try {
                const response = await Api.get(`api/show/cargos`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                // dataCargos tem que ser um array de objetos com as chaves 'value' e 'label'
                setDataCargos(response.data.map((cargo) => {
                    return {
                        value: cargo.id,
                        label: cargo.nome
                    };
                }
                ));
            } catch (error) {
                console.error(error);
            }
        }
        fetchData();
    }, [token]);

    const [dataGestores, setDataGestores] = useState([]);
    useEffect(() => {
        async function fetchData() {
            try {
                const response = await Api.get(`api/show/gestores`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setDataGestores(response.data.map((cargo) => {
                    return {
                        value: cargo.id,
                        label: cargo.nome_completo
                    };
                }
                ));
            } catch (error) {
                console.error(error);
            }
        }
        fetchData();
    }, [token]);

    const handleOpenEditarUsuario = useCallback(async (original, somenteCursos = false) => {
        setEditarSomenteCursos(somenteCursos);
        setShowModalEditarUsuario(true);
        setValue('nome_completo', original.nome_completo);
        setValue('email', original.email);
        setValue('id', original.id);
        setValue('codigo', original.codigo);
        setValue('cpf', original.cpf ? formatCPF(original.cpf) : '');

        const selectNivel = dataNiveis.find(item => item.label === original.nivel);
        setValue('nivel', { value: selectNivel.label, label: selectNivel.label });

        const selectGestor = dataGestores.find(item => item.value === original.gestor_responsavel);
        setValue('gestor_responsavel', { value: selectGestor?.value, label: selectGestor?.label });

        const selectCargo = dataCargos.find(item =>
            item.label?.toUpperCase() === (original.cargo || '').toUpperCase()
        );
        setValue('cargo', { value: selectCargo?.value, label: selectCargo?.label });

        if (original.nivel === 'Gestor' || original.nivel === 'RH') {
            try {
                const response = await Api.get(`api/usuario/${original.id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const unidades = response.data?.unidades?.length
                    ? response.data.unidades
                    : [original.unidade].filter(Boolean);
                setValue('unidade', unidades.map((unidade) => ({ value: unidade, label: unidade })));
                if (original.unidade) {
                    setValue('unidade_principal', { value: original.unidade, label: original.unidade });
                } else {
                    setValue('unidade_principal', null);
                }
            } catch (error) {
                console.error('Erro ao buscar unidades do usuário:', error);
                setValue('unidade', original.unidade
                    ? [{ value: original.unidade, label: original.unidade }]
                    : []);
                setValue('unidade_principal', original.unidade
                    ? { value: original.unidade, label: original.unidade }
                    : null);
            }
        } else {
            const selectUnidade = dataUnidades.find(item => item.nome === original.unidade);
            if (selectUnidade && selectUnidade.nome) {
                setValue('unidade', { value: selectUnidade.nome, label: selectUnidade.nome });
            } else {
                setValue('unidade', { value: '', label: '' });
            }
            setValue('unidade_principal', null);
        }

        let selectedCursos = [];

        if (original.permissao_cursos && original.permissao_cursos.trim() !== "") {
            try {
                const cursosArray = JSON.parse(original.permissao_cursos);
                selectedCursos = cursosArray.map(cursoId => {
                    return dataCursos.find(curso => curso.id === cursoId);
                }).filter(Boolean);
            } catch (error) {
                console.error("Erro ao analisar permissao_cursos:", error);
            }
        }
        const cursosForSelect = selectedCursos.map(curso => ({
            value: curso.id,
            label: curso.nome
        }));
        setValue('cursos', cursosForSelect);

        if (selectCargo?.value) {
            fetchCursosRecomendados(selectCargo.value);
        } else {
            setCursosRecomendados([]);
        }
    }, [
        setValue,
        token,
        dataNiveis,
        dataGestores,
        dataCargos,
        dataUnidades,
        dataCursos,
        fetchCursosRecomendados,
    ]);

    const handleTrocarStatus = async (id) => {
        try {
            const response = await Api.post(`api/trocarStatus/${id}`, {}, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            setToast({ header: 'Sucesso!', body: response.data.message, type: 'toast-success' });
        } catch (error) {
            setToast({ header: 'Ops!', body: error.response.data.message, type: 'toast-error' });
            console.error("Erro ao trocar status:", error);
        }
        setShowModalStatus({ show: false });
        setShowToast(true);
    };

    const handleDeletarUsuario = async (id) => {
        try {
            const response = await Api.delete(`api/deletar-usuario/${id}`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            setToast({ header: 'Sucesso!', body: response.data.message, type: 'toast-success' });
        } catch (error) {
            setToast({ header: 'Ops!', body: error.response.data.message, type: 'toast-error' });
            console.error("Erro ao deletar usuário:", error);
        }
        setShowModalDeletarUsuario({ show: false });
        setConfirmDeleteText('');
        setShowToast(true);
    };

    const columns = useMemo(
        () => [
            {
                Header: () => null,
                id: 'foto',
                accessor: row => row.foto,
                Cell: ({ cell: { value }, row: { original } }) => {
                    return (
                        <div className="d-flex align-items-center">
                            <div className="picture text-white bg-grey" style={{ fontSize: '22px', height: '42px', marginRight: '0px', width: '42px' }}>
                                {value ? <img className="object-fit-cover" src={`${process.env.REACT_APP_API_URL}/fotos/${value}`} alt="Profile" style={{ height: '42px', width: '42px' }} /> : <i className="bi bi-person" />}
                            </div>
                        </div>
                    )
                }
            },
            {
                Header: () => null,
                accessor: row => row.nome_completo,
                id: 'nome_completo',
                Cell: ({ cell: { value }, row: { original } }) => {

                    return (
                        <div className="d-flex align-items-center">
                            <div className="d-flex flex-column">
                                <span className="text-primary fw-semibold">{value}</span>
                                {nivel === 'Administrador' &&
                                    <span className="text-primary fs-7">
                                        Já acessou?
                                        <Badge bg={original.primeiro_login === 2 ? "success" : "danger"} className="ms-1">
                                            {original.primeiro_login === 2 ? "Sim" : "Não"}
                                        </Badge>
                                    </span>
                                }
                                <span className="text-primary fs-7">Gestor Responsável: <span className="fw-bold">{!original.gestor_nome ? 'Não Consta' : original.gestor_nome}</span></span>
                            </div>
                        </div>
                    )
                }
            },
            {
                Header: () => null,
                accessor: 'codigo'
            },
            {
                Header: () => null,
                accessor: row => row.nivel,
                id: 'nivel',
                Cell: ({ cell: { value }, row: { original } }) => {
                    const ativo = original.status === "ativo";
                    return (
                        <div className="d-flex align-items-center">
                            <div className="d-flex flex-column">
                                <span className="text-primary fw-semibold">{value === "Gestor" ? "Gestor" : value}</span>
                                <Badge bg={ativo ? "success" : "secondary"} className="mt-1 align-self-start">
                                    {ativo ? "Ativo" : "Inativo"}
                                </Badge>
                            </div>
                        </div>
                    )
                }
            },
            {
                Header: () => null,
                accessor: 'email'
            },
            {
                Header: () => null,
                accessor: 'unidade'
            },
            {
                Header: () => null,
                id: 'actions',
                accessor: row => row.id,
                Cell: ({ cell: { value }, row: { original } }) => {
                    // Alcance do Gestor sobre cada aluno (responsável / unidade / todos), calculado pela API.
                    const gestorAlcancaEdicao = nivel !== 'Gestor' || original.pode_editar !== false;
                    const gestorAlcancaReset = nivel !== 'Gestor' || original.pode_resetar_senha !== false;
                    const canEditFull = !(
                        (nivel === "Gestor" && !gestorAlcancaEdicao) ||
                        (nivel === "Gestor" && !gestorPermiteEditar) ||
                        (nivel === "RH" && original.nivel !== "Aluno" && original.nivel !== "Gestor") ||
                        (nivel === "Gestor" && original.nivel !== "Aluno") ||
                        (nivel !== "Administrador" && nivel !== "RH" && nivel !== "Gestor")
                    );
                    const canAtribuirCursosAluno = nivel === 'Gestor'
                        && gestorPermiteCursos
                        && original.nivel === 'Aluno'
                        && gestorAlcancaEdicao
                        && !gestorPermiteEditar;
                    const canResetSenha = (nivel !== 'Gestor' && canEditFull)
                        || (nivel === 'Gestor' && gestorPermiteResetarSenha && original.nivel === 'Aluno' && gestorAlcancaReset);

                    const menuItems = [
                        {
                            icon: !original.favorito ? 'star' : 'star-fill',
                            label: !original.favorito ? 'Favoritar' : 'Desfavoritar',
                            onClick: () => {
                                if (!original.favorito) {
                                    handleFavoritar(original.id);
                                } else {
                                    handleDesfavoritar(original.id);
                                }
                            }
                        },
                        {
                            divider: true
                        },
                        {
                            icon: 'person-vcard',
                            label: 'Informações',
                            onClick: () => navigate(`/usuarios/informacoes/${original.id}`)
                        },
                        {
                            icon: 'pencil',
                            label: 'Editar Usuário',
                            disabled: !canEditFull,
                            onClick: () => handleOpenEditarUsuario(original, false)
                        },
                        ...(canAtribuirCursosAluno ? [{
                            icon: 'journal-bookmark',
                            label: 'Atribuir cursos',
                            onClick: () => handleOpenEditarUsuario(original, true)
                        }] : []),
                        {
                            icon: 'key-fill',
                            label: 'Resetar senha',
                            disabled: !canResetSenha,
                            onClick: () => {
                                setShowModalResetSenha({ show: true, id: original.id, nome: original.nome_completo });
                            }
                        }
                    ];

                    if (nivel === "Administrador") {
                        menuItems.push(
                            {
                                icon: 'clipboard2-pulse-fill',
                                label: 'Ver pesquisa de satisfação',
                                onClick: () => handleShowPesquisa(original.id)
                            },
                            {
                                icon: original.status === "ativo" ? 'toggle2-on' : 'toggle2-off',
                                label: original.status === "ativo" ? 'Desativar usuário' : 'Ativar usuário',
                                disabled: !canEditFull,
                                onClick: () => {
                                    setShowModalStatus({
                                        show: true,
                                        id: original.id,
                                        nome: original.nome_completo,
                                        status: original.status
                                    });
                                }
                            },
                            {
                                divider: true
                            },
                            {
                                icon: 'trash3-fill',
                                label: 'Deletar usuário',
                                onClick: () => {
                                    setShowModalDeletarUsuario({
                                        show: true,
                                        id: original.id,
                                        nome: original.nome_completo
                                    });
                                }
                            }
                        );
                    }

                    return (
                        <div style={{ position: 'static' }} className="d-flex align-items-center justify-content-center">
                            <DropdownMenu items={menuItems} />
                        </div>
                    );
                }
            }


        ],
        [handleDesfavoritar, handleFavoritar, nivel, gestorPermiteEditar, gestorPermiteCursos, gestorPermiteResetarSenha, handleShowPesquisa, navigate, handleOpenEditarUsuario]
    );

    /** Tipo de usuário no cadastro: Aluno, Gestor, RH (Admin inclui Administrador). */
    const filterTipoPerfil = useMemo(() => {
        const base = [
            { value: "Aluno", label: "Aluno" },
            { value: "Gestor", label: "Gestor" },
            { value: "RH", label: "RH" },
            { value: "Administrador", label: "Administrador" },
        ];
        if (nivel === "Administrador") return base;
        if (nivel === "RH") return base.filter((o) => o.value !== "Administrador");
        if (nivel === "Gestor") return base.filter((o) => o.value === "Aluno" || o.value === "Gestor");
        return base.filter((o) => o.value === "Aluno");
    }, [nivel]);

    const exibirFiltroTipoPerfil = nivel === "Administrador" || nivel === "RH" || nivel === "Gestor";

    const handleExportarUsuariosExcel = useCallback(async () => {
        setExportandoUsuarios(true);
        try {
            const resultado = await exportarUsuariosExcel(token);
            setToast({
                header: 'Sucesso!',
                body: `Planilha gerada com ${resultado.total} usuário(s).`,
                type: 'toast-success',
            });
            setShowToast(true);
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Erro ao exportar usuários.';
            setToast({ header: 'Ops!', body: errorMessage, type: 'toast-error' });
            setShowToast(true);
        } finally {
            setExportandoUsuarios(false);
        }
    }, [token]);

    useEffect(() => {
        if (!filterTipoPerfil.some((o) => o.value === selectedFilter)) {
            setSelectedFilter(filterTipoPerfil[0]?.value ?? "Aluno");
        }
    }, [filterTipoPerfil, selectedFilter]);

    return (
        <>
            <Toast
                show={showToast}
                onClose={() => setShowToast(false)}
                delay={4000}
                autohide
                className={`position-fixed top-0 end-0 m-3 toast-icon ${toast.type || 'toast-error'}`}
                style={{ zIndex: 1060 }}
            >
                <Toast.Header>
                    <strong className="me-auto">{toast.header}</strong>
                </Toast.Header>
                <Toast.Body>{toast.body}</Toast.Body>
            </Toast>

            <div className="fade-in w-100 mt-3" style={{ marginBottom: '100px' }}>
                <div className="rounded-3 mb-4">
                    <div className="mb-3 d-flex justify-content-between align-items-start gap-3 flex-wrap">
                        <div>
                            <h1 className="text-primary fs-2 fw-bold mb-2">Lista de Usuários</h1>
                            <p className="text-muted mb-0">
                                Consulte os usuários cadastrados, filtre, edite e exclua.
                            </p>
                        </div>
                        {podeImportarUsuarios && (
                            <button
                                type="button"
                                className="d-flex align-items-center justify-content-center btn btn-primary text-white fw-semibold"
                                onClick={() => setShowModalImportar(true)}
                            >
                                <i className="bi bi-upload me-2"></i>Importar usuários
                            </button>
                        )}
                    </div>
                </div>

                {/* Barra de Filtros Compacta */}
                <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body p-3">
                        <div className="row g-2 align-items-end">
                            <div className="col-md-4">
                                <label className="form-label text-primary fw-medium small mb-1">Buscar</label>
                                <input
                                    type="search"
                                    className="form-control"
                                    placeholder="Pesquise pelo nome desejado..."
                                    value={globalFilter || ""}
                                    onChange={(e) => setGlobalFilter(e.target.value)}
                                />
                            </div>
                            {exibirFiltroTipoPerfil && (
                                <div className="col-md-2">
                                    <label className="form-label text-primary fw-medium small mb-1">Tipo</label>
                                    <Select
                                        value={filterTipoPerfil.find((opt) => opt.value === selectedFilter)}
                                        onChange={(selected) => setSelectedFilter(selected ? selected.value : "Aluno")}
                                        options={filterTipoPerfil}
                                        styles={customStyles}
                                        placeholder="Tipo"
                                        isClearable={false}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                </div>
                            )}
                            <div className="col-md-2">
                                <label className="form-label text-primary fw-medium small mb-1">Lista</label>
                                <Select
                                    value={filterLista.find((opt) => opt.value === selectedTipo)}
                                    onChange={(selected) => setSelectedTipo(selected ? selected.value : DEFAULT_LISTA_TIPO)}
                                    options={filterLista}
                                    styles={customStyles}
                                    placeholder="Lista"
                                    isClearable={false}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            </div>
                            <div className="col-md-4">
                                <div className="d-flex gap-2">
                                    <button
                                        className="btn btn-outline-primary flex-fill py-2"
                                        type="button"
                                        onClick={() => setShowDrawerFiltros(true)}
                                    >
                                        <i className="bi bi-funnel me-1"></i>
                                        Mais filtros
                                    </button>
                                    {podeExportarUsuarios && (
                                        <button
                                            className="btn btn-success flex-fill py-2"
                                            type="button"
                                            onClick={handleExportarUsuariosExcel}
                                            disabled={exportandoUsuarios || loading}
                                            title="Baixar Excel com todos os usuários cadastrados"
                                        >
                                            {exportandoUsuarios ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                                    Exportando...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="bi bi-file-earmark-excel me-1"></i>
                                                    Excel
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Chips de Filtros Ativos */}
                {(selectedUnidade || (exibirFiltroTipoPerfil && selectedFilter !== "Aluno")) && (
                    <div className="mb-3 d-flex flex-wrap gap-2">
                        {exibirFiltroTipoPerfil && selectedFilter !== "Aluno" && (
                            <Badge bg="primary" className="d-flex align-items-center gap-1" style={{ fontSize: "0.875rem", padding: "0.5rem 0.75rem" }}>
                                Tipo: {filterTipoPerfil.find((f) => f.value === selectedFilter)?.label}
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    style={{ fontSize: "0.6rem" }}
                                    onClick={() => setSelectedFilter("Aluno")}
                                    aria-label="Remover filtro"
                                ></button>
                            </Badge>
                        )}
                        {selectedUnidade && (
                            <Badge bg="primary" className="d-flex align-items-center gap-1" style={{ fontSize: '0.875rem', padding: '0.5rem 0.75rem' }}>
                                Unidade: {selectedUnidade.label}
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    style={{ fontSize: '0.6rem' }}
                                    onClick={() => setSelectedUnidade(null)}
                                    aria-label="Remover filtro"
                                ></button>
                            </Badge>
                        )}
                    </div>
                )}

                {/* Drawer de Filtros Avançados */}
                <Offcanvas show={showDrawerFiltros} onHide={() => setShowDrawerFiltros(false)} placement="end">
                    <Offcanvas.Header closeButton>
                        <Offcanvas.Title>Filtros Avançados</Offcanvas.Title>
                    </Offcanvas.Header>
                    <Offcanvas.Body className="d-flex flex-column">
                        <div className="mb-3">
                            <label className="form-label text-primary fw-medium">Unidade</label>
                            <Select
                                value={selectedUnidade}
                                onChange={(selected) => setSelectedUnidade(selected)}
                                options={dataUnidades.map(u => ({ value: u.nome, label: u.nome }))}
                                styles={customStyles}
                                placeholder="Todas as unidades"
                                isClearable
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label text-primary fw-medium">Registros por página</label>
                            <select
                                className="form-select"
                                value={pageSize}
                                onChange={(e) => setPageSize(Number(e.target.value))}
                            >
                                {[...PAGE_SIZE_OPTIONS].map(size => (
                                    <option key={size} value={size}>{size}</option>
                                ))}
                            </select>
                        </div>
                        <div className="mt-auto pt-3 border-top">
                            <div className="d-flex gap-2">
                                <button
                                    className="btn btn-outline-secondary flex-fill"
                                    onClick={() => {
                                        setSelectedUnidade(null);
                                        setPageSize(DEFAULT_PAGE_SIZE);
                                    }}
                                >
                                    Limpar filtros
                                </button>
                                <button
                                    className="btn btn-primary flex-fill"
                                    onClick={() => setShowDrawerFiltros(false)}
                                >
                                    Aplicar filtros
                                </button>
                            </div>
                        </div>
                    </Offcanvas.Body>
                </Offcanvas>

                <div className={loading ? "" : "d-none"}>
                    <LoadingScreen />
                </div>
                <div className={loading ? "d-none" : ""}>
                    <TableUsuarios
                        columns={columns}
                        data={filteredDataUsuarios}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                    />
                </div>
            </div>

            <Modal size="lg" show={showModalEditarUsuario} onHide={handleFecharModalEditarUsuario} backdrop="static">
                <form onSubmit={handleSubmit(onSubmitEditarUsuario)}>
                    <Modal.Header closeButton>
                        <span className="fw-semibold fs-5 text-primary">
                            {editarSomenteCursos ? 'Atribuir Cursos' : 'Editar Usuário'}
                        </span>
                    </Modal.Header>
                    <Modal.Body className="bg-gray-100 p-4">
                        {editarSomenteCursos ? (
                            <div className="container">
                                <div className="alert alert-info mb-4">
                                    Selecione os cursos que deseja vincular ao aluno. Demais dados do cadastro não serão alterados.
                                </div>
                                <div className="mb-3">
                                    <label className="form-label text-primary fw-medium">Aluno</label>
                                    <input
                                        type="text"
                                        className="form-control border-new-light"
                                        value={watch('nome_completo') || ''}
                                        readOnly
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label text-primary fw-medium">Cursos</label>
                                    <Controller
                                        name="cursos"
                                        control={control}
                                        defaultValue={[]}
                                        rules={{ required: true }}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                isMulti
                                                options={dataCursos.map((value) => ({
                                                    value: value.id,
                                                    label: `${value.nome}`,
                                                }))}
                                                value={field.value}
                                                styles={customStyles}
                                                onChange={value => field.onChange(value)}
                                                noOptionsMessage={() => "Nenhum curso encontrado."}
                                                placeholder="Selecione os cursos"
                                            />
                                        )}
                                    />
                                    {errors.cursos && <span className='text-danger fs-7'>Selecione pelo menos um curso.</span>}
                                </div>
                            </div>
                        ) : (
                        <div className="container">
                            <div className="row">
                                <div className="col-md-12">
                                    <div className="body">
                                        <div className="d-flex">
                                            <div className="d-flex flex-column w-100">
                                                <div className="row mb-3">
                                                    <div className="col-12">
                                                        <div className="mb-3">
                                                            <label className="form-label text-primary fw-medium">Nome</label>
                                                            <input type="text" {...register("nome_completo", { required: true })} placeholder="Nome do usuário" className="form-control border-new-light" />
                                                            {errors.nome_completo && <span className='text-danger fs-7'>Nome completo é obrigatório.</span>}
                                                        </div>
                                                    </div>
                                                    <div className="col-12 col-md-6">
                                                        <div className="mb-3">
                                                            <label className="form-label text-primary fw-medium">CPF</label>
                                                            <Controller
                                                                name="cpf"
                                                                control={control}
                                                                rules={{
                                                                    required: nivel !== 'Administrador' ? 'CPF é obrigatório' : false,
                                                                    validate: (value) => {
                                                                        const cpfLimpo = value?.replace(/\D/g, '');
                                                                        // Se for Administrador e CPF estiver vazio, permite
                                                                        if (nivel === 'Administrador' && !cpfLimpo) {
                                                                            return true;
                                                                        }
                                                                        if (!cpfLimpo || cpfLimpo.length !== 11) {
                                                                            return 'CPF deve ter 11 dígitos';
                                                                        }
                                                                        if (!validarCPF(value)) {
                                                                            return 'CPF inválido';
                                                                        }
                                                                        return true;
                                                                    }
                                                                }}
                                                                render={({ field: { onChange, value } }) => (
                                                                    <input
                                                                        type="text"
                                                                        value={value || ''}
                                                                        onChange={(e) => {
                                                                            const formatted = formatCPF(e.target.value);
                                                                            onChange(formatted);
                                                                        }}
                                                                        placeholder="000.000.000-00"
                                                                        className="form-control border-new-light"
                                                                        maxLength={14}
                                                                    />
                                                                )}
                                                            />
                                                            {errors.cpf && <span className='text-danger fs-7'>{errors.cpf.message}</span>}
                                                        </div>
                                                    </div>
                                                    <div className="col-12 col-md-6">
                                                        <div className="mb-3">
                                                            <label className="form-label text-primary fw-medium">Matrícula</label>
                                                            <input type="text" {...register("codigo", { required: true })} readOnly={nivel !== "Administrador"} className="form-control border-new-light" />
                                                            {errors.codigo && <span className='text-danger fs-7'>Código é obrigatório.</span>}
                                                        </div>
                                                    </div>
                                                    <div className="col-12">
                                                        <div className="mb-3">
                                                            <label className="form-label text-primary fw-medium">Email</label>
                                                            <input type="email" {...register("email", { required: true })} placeholder="josedasilva@email.com" className="form-control border-new-light" />
                                                            {errors.email && <span className='text-danger fs-7'>Email é obrigatório.</span>}
                                                        </div>
                                                    </div>
                                                    <div className="col-12 col-md-6">
                                                        <div className="mb-3">
                                                            <label className="form-label text-primary fw-medium">Cargo</label>
                                                            <Controller
                                                                name="cargo"
                                                                control={control}
                                                                defaultValue={[]}
                                                                rules={{ required: true }}
                                                                render={({ field }) => (
                                                                    <Select
                                                                        {...field}
                                                                        options={dataCargos}
                                                                        value={field.value}
                                                                        styles={customStyles}
                                                                        placeholder="Selecione um cargo"
                                                                        noOptionsMessage={() => "Nenhum cargo encontrado."}
                                                                        onChange={async (value) => {
                                                                            field.onChange(value);
                                                                            const nivelAtual = watch('nivel');
                                                                            if (nivelAtual?.label === 'Aluno' || nivelAtual?.value === 'Aluno') {
                                                                                const recomendados = await fetchCursosRecomendados(value?.value);
                                                                                const atuais = watch('cursos') || [];
                                                                                setValue('cursos', mergeCursosComRecomendados(atuais, recomendados));
                                                                            }
                                                                        }}
                                                                    />
                                                                )}
                                                            />
                                                            {errors.cargo && <span className='text-danger fs-7'>Cargo é obrigatório.</span>}
                                                        </div>
                                                    </div>

                                                    <div className="col-12 col-md-6">
                                                        <div className="mb-3">
                                                            <label className="form-label text-primary fw-medium">Tipo Usuário</label>
                                                            <Controller
                                                                name="nivel"
                                                                control={control}
                                                                defaultValue={[]}
                                                                rules={{ required: true }}
                                                                render={({ field }) => (
                                                                    <Select
                                                                        {...field}
                                                                        options={dataNiveis}
                                                                        value={field.value}
                                                                        styles={customStyles}
                                                                        onChange={async (value) => {
                                                                            if (value.label === 'Aluno' || value.value === 'Aluno') {
                                                                                const cargoAtual = watch('cargo');
                                                                                if (cargoAtual?.value) {
                                                                                    const recomendados = await fetchCursosRecomendados(cargoAtual.value);
                                                                                    const atuais = watch('cursos') || [];
                                                                                    setValue('cursos', mergeCursosComRecomendados(atuais, recomendados));
                                                                                }
                                                                            } else {
                                                                                setCursosRecomendados([]);
                                                                            }

                                                                            if (ehMultiUnidade(value?.value || value?.label)) {
                                                                                setValue('unidade', []);
                                                                                setValue('unidade_principal', null);
                                                                            } else if (ehMultiUnidade(nivelAtualForm)) {
                                                                                setValue('unidade', null);
                                                                                setValue('unidade_principal', null);
                                                                            }

                                                                            field.onChange(value);
                                                                        }}
                                                                        placeholder="Selecione tipo do usuário"
                                                                    />
                                                                )}
                                                            />
                                                            {errors.nivel && <span className='text-danger fs-7'>Tipo de usuário é obrigatório.</span>}
                                                        </div>
                                                    </div>

                                                    <div className="col-12 col-md-6">
                                                        <div className="mb-3">
                                                            <label className="form-label text-primary fw-medium">
                                                                Unidade{ehMultiUnidade(nivelAtualForm) ? 's' : ''}
                                                            </label>
                                                            <Controller
                                                                name="unidade"
                                                                control={control}
                                                                defaultValue={[]}
                                                                rules={{
                                                                    required: 'Selecione ao menos uma unidade',
                                                                    validate: (value) => {
                                                                        const isGestor = ehMultiUnidade(nivelAtualForm);
                                                                        if (isGestor) {
                                                                            return (Array.isArray(value) && value.length > 0) || 'Selecione ao menos uma unidade';
                                                                        }
                                                                        return value?.value ? true : 'Selecione uma unidade';
                                                                    }
                                                                }}
                                                                render={({ field }) => (
                                                                    <Select
                                                                        {...field}
                                                                        isMulti={ehMultiUnidade(nivelAtualForm)}
                                                                        options={dataUnidades.map((value) => ({
                                                                            value: value.nome,
                                                                            label: `${value.nome}`,
                                                                        }))}
                                                                        value={field.value}
                                                                        styles={customStyles}
                                                                        onChange={(value) => {
                                                                            const isGestor = ehMultiUnidade(nivelAtualForm);
                                                                            const newValue = value || (isGestor ? [] : null);
                                                                            field.onChange(newValue);
                                                                            if (isGestor) {
                                                                                syncUnidadePrincipal(newValue, watch('unidade_principal'), true);
                                                                            }
                                                                        }}
                                                                        placeholder={ehMultiUnidade(nivelAtualForm) ? 'Selecione as unidades' : 'Selecione uma unidade'}
                                                                        noOptionsMessage={() => "Nenhuma unidade encontrada."}
                                                                    />
                                                                )}
                                                            />
                                                            {errors.unidade && <span className='text-danger fs-7'>{errors.unidade.message}</span>}
                                                        </div>
                                                    </div>

                                                    {isGestorEdit && Array.isArray(watchedUnidadeEdit) && watchedUnidadeEdit.length >= 2 && (
                                                        <div className="col-12 col-md-6">
                                                            <div className="mb-3">
                                                                <label className="form-label text-primary fw-medium">Unidade principal</label>
                                                                <Controller
                                                                    name="unidade_principal"
                                                                    control={control}
                                                                    defaultValue={null}
                                                                    rules={{
                                                                        required: 'Selecione a unidade principal',
                                                                        validate: (value) => {
                                                                            if (!value?.value) {
                                                                                return 'Selecione a unidade principal';
                                                                            }
                                                                            const valores = (watchedUnidadeEdit || []).map((item) => item.value);
                                                                            return valores.includes(value.value) || 'A unidade principal deve estar entre as unidades selecionadas';
                                                                        }
                                                                    }}
                                                                    render={({ field }) => (
                                                                        <Select
                                                                            {...field}
                                                                            options={(watchedUnidadeEdit || []).map((item) => ({
                                                                                value: item.value,
                                                                                label: item.label,
                                                                            }))}
                                                                            value={field.value}
                                                                            styles={customStyles}
                                                                            onChange={(value) => field.onChange(value || null)}
                                                                            placeholder="Selecione a unidade principal"
                                                                            noOptionsMessage={() => "Nenhuma unidade selecionada."}
                                                                        />
                                                                    )}
                                                                />
                                                                {errors.unidade_principal && <span className='text-danger fs-7'>{errors.unidade_principal.message}</span>}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {(selectedFilter === 'Aluno' || (watch('nivel')?.label === 'Aluno')) && (
                                                    <div className="col-12 col-md-6">
                                                        <div className="mb-3">
                                                                <label className="form-label text-primary fw-medium">Gestor Responsável</label>
                                                            <Controller
                                                                    name="gestor_responsavel"
                                                                control={control}
                                                                defaultValue={[]}
                                                                rules={{ required: true }}
                                                                render={({ field }) => (
                                                                    <Select
                                                                        {...field}
                                                                            options={dataGestores}
                                                                        value={field.value}
                                                                        styles={customStyles}
                                                                            placeholder="Selecione um gestor"
                                                                            onChange={async (value) => {
                                                                            field.onChange(value);
                                                                                
                                                                                // Se for RH ou Administrador, buscar e pré-selecionar a unidade do gestor
                                                                                if ((nivel === 'Administrador' || nivel === 'RH') && value && value.value && dataUnidades && dataUnidades.length > 0) {
                                                                                    try {
                                                                                        const response = await Api.get(`api/usuario/${value.value}`, {
                                                                                            headers: {
                                                                                                Authorization: `Bearer ${token}`
                                                                                            }
                                                                                        });
                                                                                        
                                                                                        if (response.data && response.data.unidade) {
                                                                                            // Buscar a unidade pelo nome
                                                                                            const unidadeEncontrada = dataUnidades.find(item => item.nome === response.data.unidade);
                                                                                            if (unidadeEncontrada) {
                                                                                                const unidadeFormatada = {
                                                                                                    value: unidadeEncontrada.nome,
                                                                                                    label: unidadeEncontrada.nome
                                                                                                };
                                                                                                setValue('unidade', unidadeFormatada);
                                                                                            }
                                                                                        }
                                                                                    } catch (error) {
                                                                                        console.error('Erro ao buscar unidade do gestor:', error);
                                                                                    }
                                                                                }
                                                                            }}
                                                                            noOptionsMessage={() => "Nenhum gestor encontrado."}
                                                                    />
                                                                )}
                                                            />
                                                                {errors.gestor_responsavel && <span className='text-danger fs-7'>Gestor é obrigatório.</span>}
                                                        </div>
                                                    </div>
                                                    )}
                                                    {exibirCampoCursosNoFormulario(ajustes, nivel, watch('nivel')) &&
                                                        <div className="col-12 col-md-12">
                                                            <div className="mb-3">
                                                                <label className="form-label text-primary fw-medium">
                                                                    {(watch('nivel')?.value || watch('nivel')?.label) === 'Gestor' ? 'Cursos vinculados ao gestor' : 'Cursos'}
                                                                </label>
                                                                <Controller
                                                                    name="cursos"
                                                                    control={control}
                                                                    defaultValue={[]}
                                                                    rules={{ required: cursosObrigatoriosNoFormulario(ajustes, nivel, watch('nivel')) }}
                                                                    render={({ field }) => (
                                                                        <Select
                                                                            {...field}
                                                                            isMulti
                                                                            options={dataCursos.map((value) => {
                                                                                return {
                                                                                    value: value.id,
                                                                                    label: `${value.nome}`,
                                                                                };
                                                                            })}
                                                                            value={field.value}
                                                                            styles={customStyles}
                                                                            onChange={value => field.onChange(value)}
                                                                            noOptionsMessage={() => "Nenhum curso encontrado."}
                                                                            placeholder="Selecione os cursos"
                                                                        />
                                                                    )}
                                                                />
                                                                {errors.cursos && <span className='text-danger fs-7'>Selecione pelo menos um curso.</span>}
                                                                {cursosRecomendados.length > 0 && (
                                                                    <div className="mt-2">
                                                                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-1">
                                                                            <small className="text-muted">Cursos recomendados pelo cargo:</small>
                                                                            {!confirmandoSubstituirCursos && (
                                                                                <button
                                                                                    type="button"
                                                                                    className="btn btn-link btn-sm p-0 text-primary text-decoration-none"
                                                                                    onClick={() => setConfirmandoSubstituirCursos(true)}
                                                                                >
                                                                                    <i className="bi bi-arrow-repeat me-1"></i>
                                                                                    Aplicar cursos do cargo
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                        {confirmandoSubstituirCursos ? (
                                                                            <div className="alert alert-warning py-2 px-3 mb-2">
                                                                                <p className="mb-2 fs-7">
                                                                                    Substituir a seleção atual pelos <strong>{cursosRecomendados.length}</strong> curso(s) do cargo?
                                                                                    Cursos fora do cargo serão removidos.
                                                                                </p>
                                                                                <div className="d-flex gap-2">
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn btn-sm btn-primary"
                                                                                        onClick={handleConfirmarSubstituirCursos}
                                                                                    >
                                                                                        Confirmar
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn btn-sm btn-outline-secondary"
                                                                                        onClick={() => setConfirmandoSubstituirCursos(false)}
                                                                                    >
                                                                                        Cancelar
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            cursosRecomendados.map((curso) => {
                                                                                const selecionado = (watch('cursos') || []).some((c) => c.value === curso.id);
                                                                                return (
                                                                                    <Badge
                                                                                        key={curso.id}
                                                                                        bg={selecionado ? 'success' : 'info'}
                                                                                        className="me-1 mb-1"
                                                                                    >
                                                                                        {curso.nome}
                                                                                    </Badge>
                                                                                );
                                                                            })
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="justify-content-end">
                        <div className="d-flex justify-content-end">
                            <button
                                type="button"
                                className="btn me-2"
                                onClick={handleFecharModalEditarUsuario}
                                disabled={loadingSave}
                            >
                                Cancelar
                            </button>
                        <button
                            type="submit"
                                className="btn btn-primary"
                            disabled={loadingSave}
                        >
                            {loadingSave ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Salvando...
                                </>
                            ) : (
                                editarSomenteCursos ? 'Salvar cursos' : 'Salvar'
                            )}
                        </button>
                        </div>
                    </Modal.Footer>
                </form>
            </Modal>

            <Modal size='lg' show={showModalResetSenha.show} onHide={() => setShowModalResetSenha({ show: false })}>
                <Modal.Header closeButton>
                    <span className="fw-semibold fs-5 text-primary">Resetar senha</span>
                </Modal.Header>
                <Modal.Body className="bg-gray-100 p-4">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="body">
                                    <div className="d-flex">
                                        <div className="d-flex flex-column w-100">
                                            <div className="row">
                                                <div className="col-12">
                                                    <p>Tem certeza que deseja resetar a senha de <b>{showModalResetSenha.nome}</b>?</p>
                                                    <p>Essa ação ira resetar a senha do usuário para o padrão. Essa ação não podera ser revertida.</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="justify-content-end">
                    <div className="d-flex justify-content-end">
                        <button type="button" className="btn me-2" onClick={() => setShowModalResetSenha({ show: false })}>Cancelar</button>
                        <button type="button" className="btn btn-primary" onClick={() => handleResetPassword(showModalResetSenha.id)}>Resetar</button>
                    </div>
                </Modal.Footer>
            </Modal>

            <Modal size="lg" show={showModalPesquisa.show} onHide={() => setShowModalPesquisa({ show: false, data: null })}>
                <Modal.Header closeButton>
                    <span className="fw-semibold fs-5 text-primary">Resultado da Pesquisa de Satisfação</span>
                </Modal.Header>
                <Modal.Body className="bg-gray-100 p-4">
                    {showModalPesquisa.data ? (
                        <div className="container">
                            {showModalPesquisa.data.length === 0 && (
                                <p className="text-center text-muted">Este usuário ainda não respondeu à pesquisa de satisfação.</p>
                            )}
                            {showModalPesquisa.data.length > 0 && (
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead>
                                            <tr>
                                                <th>Pergunta</th>
                                                <th>Resposta</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {showModalPesquisa.data.map((item, index) => (
                                                <tr key={index}>
                                                    <td className="text-primary">{item.pergunta}</td>
                                                    <td>{item.resposta}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-center">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Carregando...</span>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="justify-content-end">
                    <div className="d-flex justify-content-end">
                        <button className="btn btn-primary" onClick={() => setShowModalPesquisa({ show: false, data: null })}>
                        Fechar
                    </button>
                    </div>
                </Modal.Footer>
            </Modal>

            <Modal size='lg' show={showModalStatus.show} onHide={() => setShowModalStatus({ show: false })}>
                <Modal.Header closeButton>
                    <span className="fw-semibold fs-5 text-primary">
                        {showModalStatus.status === "ativo" ? 'Desativar' : 'Ativar'} Usuário
                    </span>
                </Modal.Header>
                <Modal.Body className="bg-gray-100 p-4">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <p>Tem certeza que deseja {showModalStatus.status ? 'desativar' : 'ativar'} o usuário <b>{showModalStatus.nome}</b>?</p>
                                <p>O usuário {showModalStatus.status ? 'não' : ''} poderá acessar o sistema após esta ação.</p>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="justify-content-end">
                    <div className="d-flex justify-content-end">
                    <button
                        type="button"
                            className="btn me-2"
                            onClick={() => setShowModalStatus({ show: false })}
                    >
                            Cancelar
                    </button>
                    <button
                        type="button"
                            className="btn btn-primary"
                            onClick={() => handleTrocarStatus(showModalStatus.id)}
                    >
                            Confirmar
                    </button>
                    </div>
                </Modal.Footer>
            </Modal>

            <Modal size='lg' show={showModalDeletarUsuario.show} onHide={() => setShowModalDeletarUsuario({ show: false })}>
                <Modal.Header closeButton>
                    <span className="fw-semibold fs-5 text-danger">Deletar Usuário</span>
                </Modal.Header>
                <Modal.Body className="bg-gray-100 p-4">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="alert alert-danger" role="alert">
                                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                    <strong className="text-danger me-3">ATENÇÃO:</strong> Esta ação é irreversível!
                                </div>
                                <p>Tem certeza que deseja deletar o usuário <b>{showModalDeletarUsuario.nome}</b>?</p>
                                <p>Ao deletar este usuário, <b>TODOS</b> os dados relacionados serão permanentemente removidos, incluindo:</p>
                                <ul className="text-danger">
                                    <li>Progresso em cursos e avaliações</li>
                                    <li>Certificados emitidos</li>
                                    <li>Conversas e mensagens</li>
                                    <li>Práticas registradas</li>
                                    <li>Biblioteca pessoal</li>
                                    <li>Histórico de atividades</li>
                                </ul>
                                <div className="mt-4">
                                    <label className="form-label text-danger fw-bold">
                                        Para confirmar, digite <b>Deletar</b> no campo abaixo:
                                    </label>
                                    <input 
                                        type="text" 
                                        className="form-control border-danger" 
                                        value={confirmDeleteText}
                                        onChange={(e) => setConfirmDeleteText(e.target.value)}
                                        placeholder="Digite: Deletar"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="justify-content-end">
                    <div className="d-flex justify-content-end">
                    <button
                        type="button"
                            className="btn me-2"
                        onClick={() => {
                            setShowModalDeletarUsuario({ show: false });
                            setConfirmDeleteText('');
                        }}
                    >
                        Cancelar
                    </button>
                        <button
                            type="button"
                            className="btn btn-danger"
                            onClick={() => handleDeletarUsuario(showModalDeletarUsuario.id)}
                            disabled={confirmDeleteText !== 'Deletar'}
                        >
                            Deletar Usuário
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>

            <ModalImportarUsuarios
                show={showModalImportar}
                onHide={() => setShowModalImportar(false)}
                token={token}
                onToast={(header, body, variant) => {
                    const type = variant === 'success' ? 'toast-success' : variant === 'warning' ? 'toast-warning' : 'toast-error';
                    setToast({ header, body, type });
                    setShowToast(true);
                }}
                onSuccess={() => setRefreshKey((k) => k + 1)}
            />
        </>
    )
}

export default ListaUsuarios;